/**
 * Zustand Store for Authentication State
 * Manages user authentication state across the application
 */

import { create } from 'zustand';
import type { User, Member } from '../types';
import { decodeJWT } from '../lib/tokenUtils';

interface AuthState {
  user: User | null;
  member: Member | null;
  token: string | null;
  tenantName: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (user: User, member: Member, token: string, tenantName?: string) => void;
  setToken: (token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  initAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  member: null,
  token: null,
  tenantName: null,
  isAuthenticated: false,
  isLoading: true,

  setAuth: (user, member, token, tenantName) => {
    // Store in localStorage (persists across tabs and browser sessions)
    localStorage.setItem('tw_access_token', token);
    localStorage.setItem('tw_user_data', JSON.stringify({ user, member, tenantName }));

    set({
      user,
      member,
      token,
      tenantName: tenantName ?? null,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  setToken: (token) => {
    // Update token in localStorage and state (used for token refresh)
    localStorage.setItem('tw_access_token', token);
    set({ token });
  },

  clearAuth: () => {
    // Clear localStorage
    localStorage.removeItem('tw_access_token');
    localStorage.removeItem('tw_user_data');

    set({
      user: null,
      member: null,
      token: null,
      tenantName: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  initAuth: () => {
    // Try to restore auth from localStorage
    const token = localStorage.getItem('tw_access_token');
    const userDataStr = localStorage.getItem('tw_user_data');

    // A recoverable session needs BOTH the token and its paired user data, and a
    // token that is at least well-formed (decodable with an `exp`). We deliberately
    // trust an *expired but well-formed* token here: the axios 401 interceptor
    // silently refreshes it via the httpOnly refresh cookie, so clearing it on
    // expiry alone would force a needless re-login (TW-277, option B1). We only
    // reject genuinely unrecoverable state — a malformed token, or a partial
    // pair (e.g. an orphan token with no user_data, which axios would otherwise
    // still attach as a bearer).
    if (token && userDataStr && decodeJWT(token)?.exp) {
      try {
        const userData = JSON.parse(userDataStr);
        set({
          user: userData.user,
          member: userData.member,
          token,
          tenantName: userData.tenantName ?? null,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      } catch (error) {
        console.error('Failed to parse user data:', error);
        // fall through to scrub below
      }
    }

    // Unrecoverable / partial / malformed state: scrub any lingering keys so no
    // orphan bearer token is left for axios to attach, then land unauthenticated.
    if (token || userDataStr) {
      localStorage.removeItem('tw_access_token');
      localStorage.removeItem('tw_user_data');
    }
    set({
      user: null,
      member: null,
      token: null,
      tenantName: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },
}));
